<template>
  <div class="customer-card" role="region">
    <!-- Loading Indicator -->
    <loading
        :active.sync="isLoading"
        :can-cancel="true"
        :is-full-page="true"
        loader="bars"
        aria-live="assertive"
        aria-busy="true"
        role="status">
    </loading>

    <CheckoutContainer :title="$t('subTitle.orderDetails')">
      <div v-if="basketTile && (basketTile.orderDate || basketTile.orderType)" class="basket-meta-static mb-2">
        <div class="d-flex flex-column gap-1 w-100">
          <div v-if="basketTile.orderDate" class="d-flex align-items-center w-100 mb-1 basket-meta-row">
            <font-awesome-icon icon="user" class="me-2 basket-meta-icon" />
            <span class="flex-grow-1 basket-meta-text basket-meta-left">{{ customer.firstName }} {{ customer.lastName }}</span>
          </div>
          <div v-if="basketTile.orderDate" class="d-flex align-items-center w-100 mb-1 basket-meta-row">
            <font-awesome-icon icon="envelope" class="me-2 basket-meta-icon" />
            <span class="flex-grow-1 basket-meta-text basket-meta-left">{{ customer.emailAddr }}</span>
          </div>
          <div v-if="basketTile.orderDate" class="d-flex align-items-center w-100 mb-1 basket-meta-row">
            <font-awesome-icon icon="calendar-alt" class="me-2 basket-meta-icon" />
            <span class="flex-grow-1 basket-meta-text basket-meta-left">{{ formatFullDate(basketTile.orderDate) }}</span>
          </div>
          <div v-if="orderType" class="d-flex align-items-center w-100 basket-meta-row">
            <font-awesome-icon icon="receipt" class="me-2 basket-meta-icon" />
            <span class="flex-grow-1 basket-meta-text basket-meta-left">{{ capitalizeFirstLetter(orderType) }}</span>
          </div>
        </div>
      </div>

      <Button v-if="orderCommentEnabled"
        secondary
        icon-left="comment"
        :icon-right="confirmedComment && confirmedComment.length ? 'pencil' : 'add'"
        @click="openCommentModal"
      >
        {{ confirmedComment && confirmedComment.length ? confirmedComment : $t('button.commentForOrderAdd') }}
      </Button>
    </CheckoutContainer>
    <br>
    <CheckoutContainer :title="$t('subTitle.paymentMethods')">
      <Button
        secondary
        icon-left="credit-card"
        :icon-right="isSinglePaymentMethod ? '' : (selectedPaymentMethod && selectedPaymentMethod.code ? 'pencil' : 'add')"
        @click="openPaymentMethodsModal"
        :disabled="isSinglePaymentMethod"
      >
        {{ isSinglePaymentMethod ? (selectedPaymentMethod.description || selectedPaymentMethod.name || selectedPaymentMethod.code) : (selectedPaymentMethod.description || selectedPaymentMethod.name || $t('button.paymentMethodAdd')) }}
      </Button>
      <Button v-if="promotionsEnabled"
        secondary
        icon-left="gift"
        :icon-right="confirmedPromotionBarcode && confirmedPromotionBarcode.length ? 'pencil' : 'add'"
        @click="openPromotionBarcodeModal"
      >
        {{ confirmedPromotionBarcode && confirmedPromotionBarcode.length ? confirmedPromotionBarcode : $t('button.promotionBarcodeAdd') }}
      </Button>
    </CheckoutContainer>
    <br>
    <div class="customerCardGrid rowGap20">
      <Basket
        :title="$t('subTitle.orderOverview')"
        :basketTile="basketTile"
        :show-vat="true"
        :editable="false"
      />
    </div>

    <i18n class="text-left" path="text.acceptTermsAndPrivacyOnOrder" tag="p">
      <template v-slot:terms>
        <a :href="config.COMPANY.links.terms" target="_blank">{{ $t('link.terms') }}</a>
      </template>
      <template v-slot:privacy>
        <a :href="config.COMPANY.links.privacy" target="_blank">{{ $t('link.privacy') }}</a>
      </template>
    </i18n>

    <Button icon-left="money-bill" @click="pay" :disabled="!selectedPaymentMethod || !selectedPaymentMethod.code">
      {{ $t('button.pay') }}
    </Button>

    <!-- Modal for adding comment to order -->
    <b-modal id="order-comment-modal" hide-footer :title="$t('subTitle.orderCommentEnter')" size="lg" centered>
      <Comment
        v-model="comment"
        :max-length="orderCommentMaxLength"
        @close="closeCommentModal"
        @comment-selected="onCommentSelected"
      />
    </b-modal>

    <!-- Modal for basket products -->
    <b-modal id="payment-methods-modal" hide-footer :title="$t('subTitle.paymentMethodSelect')" size="lg" centered>
      <PaymentMethods
        v-model="selectedPaymentMethod"
        :paymen-methods="paymentMethods"
        @close="closePaymentMethodsModal"
        @select="onPaymentMethodSelected"
      />
    </b-modal>

    <!-- Modal for adding promotion barcode to order -->
    <b-modal id="promotion-barcode-modal" hide-footer :title="$t('subTitle.promotionBarcodeEnter')" size="lg" centered>
      <PromotionBarcode
        v-model="promotionBarcode"
        @close="closePromotionBarcodeModal"
        @promotion-barcode-input="onPromotionBarcodeInput"
      />
    </b-modal>

    <component
      v-if="selectedPaymentMethod.paymentType === 4"
      :is="paymentComponent"
      :subCode="selectedPaymentMethod.subCode"
      :callback="redirectTo"
    />
  </div>
</template>

<script>
import VueLoading from "vue-loading-overlay";
import {accountService} from "@/services/api/accountService";
import {useAppStore} from "@/stores/app";
import {useShopStore} from "@/stores/shop";
import PaymentMethods from "@/components/sub-components/Checkout/PaymentMethods.vue";
import Basket from "@/components/sub-components/Basket/Basket.vue";
import {paymentService} from "@/services/api/paymentService";
import PromotionBarcode from "@/components/sub-components/Checkout/PromotionBarcode.vue";
import {OrderTypes, Workflow} from "@/constants/constants";
import basketMixin from "@/components/mixins/basketMixin";
import Button from "@/components/barrier-free-template-components/Button.vue";
import {orderService} from "@/services/api/orderService";
import CheckoutContainer from "@/components/sub-components/Container/CheckoutContainer.vue";
import Comment from "@/components/sub-components/Checkout/Comment.vue";
import CreditCardPaymentComponentSaferpay from "@/components/main-components/CreditCardPaymentComponentSaferpay.vue";
import CreditCardPaymentComponentSecurepay from "@/components/main-components/CreditCardPaymentComponentSecurepay.vue";
import CreditCardPaymentComponentNovalnet from "@/components/main-components/CreditCardPaymentComponentNovalnet.vue";
import CreditCardPaymentComponentPayone from "@/components/main-components/CreditCardPaymentComponentPayone.vue";
import CreditCardPaymentComponentAdyen from "@/components/main-components/CreditCardPaymentComponentAdyen.vue";
import {capitalizeFirstLetter} from "@/utils/utils";
import {formatFullDate} from "../../../utils/dateUtils";

export default {
  mixins: [basketMixin],
  inject: ["setPageMetaTitle"],
  components: {
    Comment,
    CheckoutContainer,
    Button,
    PromotionBarcode,
    Basket,
    PaymentMethods,
    loading: VueLoading,
    "credit-card-payment-saferpay": CreditCardPaymentComponentSaferpay,
    "credit-card-payment-securepay": CreditCardPaymentComponentSecurepay,
    "credit-card-payment-novalnet": CreditCardPaymentComponentNovalnet,
    "credit-card-payment-payone": CreditCardPaymentComponentPayone,
    "credit-card-payment-adyen": CreditCardPaymentComponentAdyen,
  },
  setup() {
    const appStore = useAppStore();
    const shopStore = useShopStore();

    return { appStore, shopStore };
  },
  data() {
    return {
      isLoading: false,
      customer:{},
      selectedPaymentMethod: {},
      paymentMethods: [],
      basketTile: null,
      promotionBarcode: '', // Use empty string for v-model
      confirmedPromotionBarcode: '', // Only set if confirmed
      comment: undefined,
      confirmedComment: null, // Only set if confirmed
      paymentComponent: 'payment-component', // Add this line, replace with actual component name if needed
      orderType: '', // new data field
      mealPlanId: '',
      reservationDateTime: '',
    };
  },
  computed: {
    config() {
      return window.config;
    },
    promotionsEnabled() {
      return this.appStore.isWorkflow(Workflow.MEAL_PLAN) ? window.config.MEAL_PLAN.checkout.promotions : window.config.ORDER.promotions;
    },
    orderCommentEnabled() {
      return this.appStore.isWorkflow(Workflow.MEAL_PLAN) ? window.config.MEAL_PLAN.checkout.comment.enabled : window.config.ORDER.promotions;
    },
    orderCommentMaxLength() {
      return this.appStore.isWorkflow(Workflow.MEAL_PLAN) ? window.config.MEAL_PLAN.checkout.comment.maxLength : window.config.ORDER.promotions;
    },
    isSinglePaymentMethod() {
      return this.paymentMethods && this.paymentMethods.length === 1;
    },
  },
  methods: {
    formatFullDate,
    capitalizeFirstLetter,
    redirectTo(status) {
      if (status >= 0) {
        this.makeToast("success", this.$t("notification.chargeSuccessful"));

        if (this.appStore.isWorkflow(Workflow.MEAL_PLAN)) {
          this.$navigateToMealPlanner(true);
        }
      }
    },
    openPaymentMethodsModal() {
      this.$bvModal.show("payment-methods-modal");
    },
    closePaymentMethodsModal() {
      this.$bvModal.hide("payment-methods-modal");
    },
    onPaymentMethodSelected(method) {
      this.selectedPaymentMethod = method;
      this.closePaymentMethodsModal();
    },
    openCommentModal() {
      // Set the comment field in the modal to the last confirmed comment value
      this.comment = this.confirmedComment || '';
      this.$bvModal.show("order-comment-modal");
    },
    closeCommentModal() {
      this.$bvModal.hide("order-comment-modal");
    },
    onCommentSelected(comment) {
      this.handleComment(comment);
    },
    openPromotionBarcodeModal() {
      // Set the promotionBarcode field in the modal to the last confirmed promotion barcode value
      this.promotionBarcode = this.confirmedPromotionBarcode || '';
      this.$bvModal.show("promotion-barcode-modal");
    },
    closePromotionBarcodeModal() {
      this.$bvModal.hide("promotion-barcode-modal");
    },
    onPromotionBarcodeInput(barcode) {
      console.log('[Checkout] onPromotionBarcodeInput called with barcode:', barcode);
      // Only update promotionBarcode if confirmed as valid in handlePromotionBarcode
      this.handlePromotionBarcode(barcode);
    },
    async fetchPaymentMethods() {
      console.log('[Checkout] fetchPaymentMethods called');
      const paymentResult = await paymentService.getPaymentMethods();
      if (paymentResult && paymentResult.paymentDetails && paymentResult.paymentDetails.length > 0) {
        this.paymentMethods = paymentResult.paymentDetails;
        if (paymentResult.paymentDetails.length === 1) {
          this.selectedPaymentMethod = paymentResult.paymentDetails[0];
        } else {
          this.selectedPaymentMethod = {};
        }
      }
    },
    async fetchCustomerDetails() {
      console.log('[Checkout] fetchCustomerDetails called');
      const result = await accountService.afterLogin();
      if (result.exception !== undefined || result.wondCustomer === undefined) {
        this.makeToast('danger', this.$t('notification.customerCard'));
        return;
      }
      this.customer = result.customer;
      // Set mealPlanId and reservationDateTime if available
      if (result.basketTile) {
        this.mealPlanId = result.basketTile.mealPlanId || '';
        this.reservationDateTime = result.basketTile.orderDate || '';
      }
    },
    async getCurrentOrder() {
      const options = {
        mealPlanId: this.mealPlanId,
        reservationDateTime: this.reservationDateTime,
      };
      this.basketTile = await this.getBasketTile(OrderTypes.DINE_IN, options);

      // Ensure comment and promotionBarcode are set from basketTile if available
      if (this.basketTile) {
        this.comment = this.basketTile.comment || '';
        this.promotionBarcode = this.basketTile.promotionBarcode || '';
        if (this.basketTile.comment && this.basketTile.comment.length) {
          this.confirmedComment = this.basketTile.comment;
        }
        if (this.basketTile.promotionBarcode && this.basketTile.promotionBarcode.length) {
          this.confirmedPromotionBarcode = this.basketTile.promotionBarcode;
        }
        this.orderType = this.basketTile.orderType || '';
        this.mealPlanId = this.basketTile.mealPlanId || '';
        this.reservationDateTime = this.basketTile.orderDate || '';
      }
    },
    async confirmOrder() {
      let options = {
        mealPlanId: this.mealPlanId ? this.mealPlanId : undefined,
        reservationDateTime: this.reservationDateTime ? this.reservationDateTime : undefined,
      };
      const result = await orderService.confirm(
        this.orderType,
        this.comment,
        this.promotionBarcode,
        true,
        options
      );
      if (result.exception !== undefined) {
        return;
      }
      this.basketTile = result.basketTile;
      if (this.basketTile) {
        this.comment = this.basketTile.comment;
        this.promotionBarcode = this.basketTile.promotionBarcode;
        this.mealPlanId = this.basketTile.mealPlanId || '';
        this.reservationDateTime = this.basketTile.orderDate || '';
      }
    },
    async handlePromotionBarcode(barcode) {
      try {
        const options = {
          mealPlanId: this.mealPlanId,
          reservationDateTime: this.reservationDateTime,
        };
        const result = await orderService.confirm(
          this.orderType,
          this.comment,
          barcode,
          true,
          options
        );
        if (result && result.exception || result.errorCode !== 0) {
          console.log('[Checkout] handlePromotionBarcode error:', result.error);
          this.$root.$emit('promotion-barcode-error', result.error);
        } else {
          this.basketTile = result.basketTile;
          this.confirmedPromotionBarcode = barcode;
          this.closePromotionBarcodeModal();
        }
      } catch (e) {
        this.$root.$emit('promotion-barcode-error', this.$t('notification.promotionBarcodeNotValid'));
      }
    },
    async handleComment(comment) {
      try {
        const options = {
          mealPlanId: this.mealPlanId,
          reservationDateTime: this.reservationDateTime,
        };
        const result = await orderService.confirm(
          this.orderType,
          comment,
          this.promotionBarcode,
          true,
          options
        );
        if (result && result.exception || result.errorCode !== 0) {
          console.log('[Checkout] handleComment error:', result.error);
          this.$root.$emit('comment-error', result.error);
        } else {
          this.basketTile = result.basketTile;
          this.confirmedComment = comment;
          this.closeCommentModal();
        }
      } catch (e) {
        this.$root.$emit('comment-error', this.$t('notification.commentNotValid'));
      }
    },
    async pay() {
      this.isLoading = true;
      const paymentMethod = this.selectedPaymentMethod.subCode ? `${this.selectedPaymentMethod.code}~~${this.selectedPaymentMethod.subCode}` : this.selectedPaymentMethod.code;
      const result = await paymentService.pay(paymentMethod);
      if (result.exception !== undefined || result.errorCode !== 0) {
        this.makeToast("danger", this.$t("error.onPayment"));
        this.isLoading = false;
        return;
      }
      this.isLoading = false;

      const paymentComponents = {
        Saferpay: "credit-card-payment-saferpay",
        Securepay: "credit-card-payment-securepay",
        Novalnet: "credit-card-payment-novalnet",
        PayOne: "credit-card-payment-payone",
        Adyen: "credit-card-payment-adyen"
      };

      if (this.selectedPaymentMethod.paymentType !== 4) {
        // If paymentType is not 4 and payment is successful, navigate
        this.makeToast("success", this.$t("notification.paymentSuccessful"));
        if (this.appStore.isWorkflow(Workflow.MEAL_PLAN)) {
          this.$navigateToMealPlanner(true);
        }
        return;
      }

      this.paymentComponent = paymentComponents[result.paymentMethod.split("~~")[0]] || null;
    },
  },
  async created() {
    await this.setPageMetaTitle(this.shopStore.name);

    try {
      await this.fetchCustomerDetails();
      await this.getCurrentOrder();
      await this.confirmOrder();
      await this.fetchPaymentMethods();
    } catch (e) {
      console.error('[Checkout] created hook error:', e);
      // handle error if needed
    }
  },
};
</script>

<style>
.customerCardGrid {
  display:flex;
  flex-wrap: wrap;
  & > * {
    flex: 1 0 320px;
  }
}

.header-title h2 {
  font-size: 24px;
  color: var(--main-color);
}

/* Customer Info List Style */
.column-right ul {
  list-style-type: none;
  padding: 0;
}

.column-right li {
  margin-bottom: 10px;
}

/* Button Style */
.column-right button {
  margin-top: 20px;
}

/* Responsive Design for Small Screens */
@media (max-width: 768px) {
  .flex-container {
    flex-direction: column;
  }

  .column {
    max-width: 100%;
  }
}

.basket-meta-static {
  background-color: #f8f9fa;
  padding: 8px 0;
  border-radius: var(--button-border-radius);
  margin-bottom: 12px;
}

.basket-meta-icon {
  color: #0057b8;
  font-size: 18px;
}

.basket-meta-text {
  color: #333;
  font-size: 16px;
}
.basket-meta-left {
  text-align: left;
  margin-left: 15px;
}
.basket-meta-row {
  width: 100%;
  margin-left: 10px
}
</style>
